import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import type { FetcherType } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import type { CourseDataInterface } from 'modules/course/types/course-interface'
import { APPLY_THEME_API, COURSE_THEMES_API, UNSET_THEME_API } from '../api/themes-api'
import type { CourseThemeInterface, ThemeCreateInterface } from '../types/theme-interface'

interface UseThemeActionsResult {
  createTheme: (theme: ThemeCreateInterface) => Promise<CourseThemeInterface>
  applyThemeToCourse: (themeId: number) => Promise<CourseDataInterface>
  deleteTheme: (themeId: number) => Promise<void>
  updateTheme: (themeId: number, theme: ThemeCreateInterface) => Promise<CourseThemeInterface>
  unsetTheme: () => Promise<ThemeCreateInterface>
}

export default function useThemeActions(): UseThemeActionsResult {
  const { courseData } = useCourseData()

  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum>({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: deleteFetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum>({
    method: RequestMethodsEnum.delete,
  })

  const { fetcher: updateFetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum,
    CourseThemeInterface
  >({ method: RequestMethodsEnum.put })

  // TODO: investigate
  const getPostFetcher = <T>(): FetcherType<RequestMethodsEnum, T> =>
    fetcher as unknown as FetcherType<RequestMethodsEnum, T>

  const createTheme = (theme: ThemeCreateInterface): Promise<CourseThemeInterface> =>
    getPostFetcher<CourseThemeInterface>()(`${COURSE_THEMES_API}/create`, theme)

  const applyTheme = async (themeId: number): Promise<CourseDataInterface> => {
    const courseId = courseData?.id ?? 0
    const fetcher = getPostFetcher<CourseDataInterface>()
    return fetcher(APPLY_THEME_API(courseId, themeId))
  }

  const deleteTheme = async (themeId: number): Promise<void> =>
    deleteFetcher(`${COURSE_THEMES_API}/${themeId}/delete`)

  const updateTheme = async (
    themeId: number,
    theme: ThemeCreateInterface,
  ): Promise<CourseThemeInterface> => updateFetcher(`${COURSE_THEMES_API}/${themeId}/update`, theme)

  const unsetTheme = async (): Promise<ThemeCreateInterface> =>
    getPostFetcher<ThemeCreateInterface>()(UNSET_THEME_API(courseData?.id ?? 0))

  return { createTheme, applyThemeToCourse: applyTheme, deleteTheme, updateTheme, unsetTheme }
}
